import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { CrehanaNextSeo } from '@crehana/next';
import type { TAppProps } from '@crehana/next';
import { MainFooter, useMe } from '@crehana/web';
import { useTranslation } from '@crehana/i18n';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';

import { useIntersectionObserver } from '@crehana/react-hooks';

import { customSessionStorage } from '@crehana/utils';

import MainMenu from '@/shared/components/MainMenu';
import SEOFooter from '@/shared/components/SEOFooter';

import { TrackUserScroll } from '@/shared/components';
import { getBaseUrl } from '@/shared/utils';

import { HOMEPAGE_AMPLITUDE_PAGE_NAME, SEO_DEFAULTS } from './constants';

import type { IAppProps } from './types';
import Hero from './sections/Hero';
import Product from './sections/Product';
import Banner from './sections/Banner';
import PreFooter from './sections/PreFooter';
import Companies from './sections/Companies';
import Leaders from './sections/Leaders';
import WhyCrehana from './sections/WhyCrehana';
import { mainMenuDarkCustomCss, mainMenuLightCustomCss } from './App.styles';
import CompaniesLogos from './sections/CompaniesLogos';
import { syncUtmsToSessionStorage } from './utils';

const App: React.FC<TAppProps<IAppProps>> = ({
  SEOData,
  companiesLogos,
  featureFlags,
}) => {
  const { loading, me } = useMe();
  const { logEvent } = useAmplitude();
  const { t, countryCode } = useTranslation();
  const nextRouter = useRouter();

  const [navbarLight, setNavbarLight] = useState(false);
  const [courseExplorerOpen, setCourseExplorerOpen] = useState(false);
  const setNavbarLightRef = useRef(null);
  const navbarObserver = useIntersectionObserver(setNavbarLightRef, {});

  useEffect(() => {
    if (!loading) {
      if (me) {
        // redirect to homeuser if the user is logged
        const url = me.organization
          ? `/org/${me.organization.slug}/learn/`
          : '/home/';

        nextRouter.push(url);
      } else {
        logEvent({
          event: 'Render Home Landing',
          data: getSourceEventParams(),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, me]);

  useEffect(() => {
    if (
      navbarObserver?.intersectionRatio === 0 &&
      navbarObserver?.boundingClientRect.top < 0
    ) {
      setNavbarLight(true);
    } else {
      setNavbarLight(false);
    }
  }, [navbarObserver?.isIntersecting]);

  // We are persisting these utm values to be read at the Onboarding page
  useEffect(() => {
    const queryParamsKeys = Object.keys(
      Object.fromEntries(new URLSearchParams(window.location.search) || []),
    ).map(el => el.toLocaleLowerCase());

    const hasUTMsOrGClid =
      queryParamsKeys.find(
        paramKey => paramKey.startsWith('utm') || paramKey.startsWith('gclid'),
      ) !== undefined;

    const isOrganicSearch =
      document.referrer.search(/(www\.google\..*)/) > 0 ||
      document.referrer.search(/(www\.bing\..*)/) > 0;

    if (isOrganicSearch && !hasUTMsOrGClid) {
      customSessionStorage.setItem('utm_source', 'organic-search');
    } else {
      syncUtmsToSessionStorage([
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
      ]);
    }
  }, []);

  const SITE_URL_WITH_PREFIX = getBaseUrl(countryCode);

  const navbarShouldBeLight = courseExplorerOpen || navbarLight;

  return (
    <div className="relative">
      <CrehanaNextSeo
        hasCountryCode
        title={SEOData.metaTitle}
        description={SEOData.metaDescription}
        twitter={{
          cardType: 'summary',
          site: '@crehana',
          handle: '@crehana',
        }}
        openGraph={{
          url: `${SITE_URL_WITH_PREFIX}/`,
          title: t('SEO_TITLE'),
          description: t('SEO_DESCRIPTION'),
          images: [
            {
              url: SEO_DEFAULTS.og_data.image.url,
              alt: SEO_DEFAULTS.og_data.image.alt,
            },
          ],
          type: SEO_DEFAULTS.og_data.type,
          site_name: SEO_DEFAULTS.og_data.site_name,
        }}
        additionalMetaTags={[{ property: 'lang', content: 'es' }]}
      />
      <div
        className="absolute"
        style={{ top: '12px' }}
        ref={setNavbarLightRef}
      />
      <MainMenu
        amplitudePageName={HOMEPAGE_AMPLITUDE_PAGE_NAME}
        position="sticky"
        isTransparent={!navbarShouldBeLight}
        theme={navbarShouldBeLight ? 'light' : 'dark'}
        css={
          navbarShouldBeLight ? mainMenuLightCustomCss : mainMenuDarkCustomCss
        }
        customUrls={{
          register: '/onboarding/',
          premiumLanding: '/planes/',
          B2BLanding: '/premium/',
        }}
        customLocales={{
          es: {
            'main-menu': {
              B2B_LANDING_LINK_LABEL: 'Para personas',
              PREMIUM_LANDING_LINK_LABEL: 'Para empresas',
              // El label "Prueba gratis" se eligio por sobre "Registrate" segun los resultados de este A/B:
              // https://experiment.amplitude.com/crehana/314551/config/10896/analyze
              REGISTER_LABEL: 'Prueba gratis',
            },
          },
          pt: {
            'main-menu': {
              B2B_LANDING_LINK_LABEL: 'Para pessoas',
              PREMIUM_LANDING_LINK_LABEL: 'Para empresas',
              REGISTER_LABEL: 'Experimente de graça',
            },
          },
        }}
        onChangeStateOpenOverlay={(_, courseExplorerStatus) =>
          setCourseExplorerOpen(courseExplorerStatus)
        }
        layoutConfig={{
          hideSearch: true,
          hideShoppingCart: true,
          hideResourcesDropdown: true,
          hideDegreesDropdown: true,
          renderCatalogAtEnd: true,
          hideHigherEdLink: false,
          hideSolutionsMenu: false,
        }}
      />
      <Hero />
      {!companiesLogos ? null : (
        <CompaniesLogos
          logos={companiesLogos}
          enableLogosFromAdmin={featureFlags?.featureFlagLogos}
        />
      )}
      <TrackUserScroll
        amplitudePageName={HOMEPAGE_AMPLITUDE_PAGE_NAME}
        sectionName="Logos End"
      />
      <Product />
      <TrackUserScroll
        amplitudePageName={HOMEPAGE_AMPLITUDE_PAGE_NAME}
        sectionName="Product End"
      />
      <Banner />
      <WhyCrehana />
      <TrackUserScroll
        amplitudePageName={HOMEPAGE_AMPLITUDE_PAGE_NAME}
        sectionName="Why Crehana End"
      />
      <Companies />
      <TrackUserScroll
        amplitudePageName={HOMEPAGE_AMPLITUDE_PAGE_NAME}
        sectionName="Companies Bubbles End"
      />
      <Leaders />
      <TrackUserScroll
        amplitudePageName={HOMEPAGE_AMPLITUDE_PAGE_NAME}
        sectionName="Leaders End"
      />
      <PreFooter />
      <TrackUserScroll
        amplitudePageName={HOMEPAGE_AMPLITUDE_PAGE_NAME}
        sectionName="Prefooter End"
      />
      <div className="relative z-2 mt-48">
        <SEOFooter isDark={false} seoData={SEOData.footer} />
        <TrackUserScroll
          amplitudePageName={HOMEPAGE_AMPLITUDE_PAGE_NAME}
          sectionName="SEO Footer End"
        />
        <MainFooter />
      </div>
    </div>
  );
};

export default App;
